import { Switch } from "@mui/material";
import { useEffect, useState } from "react";
import { RxPencil1 } from "react-icons/rx";
import { useDispatch, useSelector } from "react-redux";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import {
  setIsTableLoading,
  setItems,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setTotalItems,
} from "src/redux/slices/UserSlice";
import { RootState } from "src/redux/store";
import {
  useGetUserQuery,
  useUserStatusChangeMutation,
} from "src/services/UserServices";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import AddUserFormWrapper from "../Add/AddUserFormWrapper";
import EditUserFormWrapper from "../Edit/EditUserFormWrapper";
import UserListing from "./UserListing";
import Chip from "@mui/material/Chip";
import {
  UserModuleActionTypes,
  UserModuleNameTypes,
} from "src/models/userAccess/UserAccess.model";
import { isCheckColumnPermission } from "src/helpers/userAccessFun";
import { TbPasswordMobilePhone } from "react-icons/tb";
import ChangePasswordDialogFormWrapper from "./ChangePasswordDialog/ChangePasswordDialogFormWrapper";

const UserListingWrapper = () => {
  const dispatch = useDispatch();
  //  state
  const [isOpenAddForm, setIsOpenAddForm] = useState(false);
  const [isOpenEditForm, setIsOpenEditForm] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [isOpenChangePasswordDialog, setIsOpenChangePasswordDialog] =
    useState<boolean>(false);

  const { items, totalItems, page, rowsPerPage, searchValue, isTableLoading } =
    useSelector((state: RootState) => state.user);
  const [statusChange] = useUserStatusChangeMutation();
  const { data, isLoading, isFetching } = useGetUserQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["firstName", "lastName", "mobile", "email"],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  const getActionOptions = (userId: string) => {
    return [
      {
        label: (
          <div className="flex gap-2 items-center text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () => {
          setIsOpenEditForm(true);
          setSelectedUserId(userId);
        },
        actionName: UserModuleActionTypes.DELETE,
        moduleName: UserModuleNameTypes.USER,
      },
      {
        label: (
          <div className="flex gap-2 items-center text-secondary-main">
            <TbPasswordMobilePhone className="text-lg" /> Change Password
          </div>
        ),
        onClick: () => {
          setIsOpenChangePasswordDialog(true);
          setSelectedUserId(userId);
        },
        actionName: UserModuleActionTypes.DELETE,
        moduleName: UserModuleNameTypes.USER,
      },
    ];
  };
  const handleActiveDeactive = (userId: string, isActive: boolean) => {
    showConfirmationDialog({
      title: "Heads Up",
      text: `Are you sure, you want to ${
        isActive ? "deactive" : "active"
      } this user ?`,
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result.isConfirmed) {
          statusChange(userId).then((res: any) => {
            if (res?.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              if (res?.data?.status) {
                showToast("success", res?.data?.message);
              } else {
                showToast("error", res?.data?.message);
              }
            }
          });
        }
      },
    });
  };

  const columns: columnTypes[] = [
    {
      field: "name",
      headerName: "Name",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
      renderCell: (row: any) => {
        return <span>{row?.firstName + " " + row?.lastName}</span>;
      },
    },
    {
      field: "userName",
      headerName: "User Name",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
    },
    {
      field: "email",
      headerName: "Email",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
    },
    {
      field: "mobile",
      headerName: "Mobile",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
    },
    {
      field: "userRole",
      headerName: "Role",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
    },
    {
      field: "branches",
      headerName: "Branches",
      flex: "flex-[1_1_0%]",
      extraClasses: "flex-wrap py-1",
      renderCell: (row: any) => {
        return (
          <>
            {row?.branches?.map((branch: any) => {
              return (
                <Chip
                  key={branch?._id}
                  label={branch?.branchName}
                  className="me-2 my-1"
                />
              );
            })}
          </>
        );
      },
    },
    {
      field: "active/deactive",
      headerName: "Active/Deactive",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
      renderCell: (row: any) => {
        return (
          <Switch
            checked={row?.isActive}
            onChange={(e) => handleActiveDeactive(row?._id, row?.isActive)}
          />
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: "flex-[0.25_0.25_0%]",
      extraClasses: "",
      renderCell: (row: any) => {
        const options = getActionOptions(row?._id);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];

  useEffect(() => {
    if (!isLoading && !isFetching) {
      dispatch(setItems(data?.data || []));
      dispatch(setIsTableLoading(false));
      dispatch(setTotalItems(data?.totalItem));
      dispatch(setRowsPerPage(data?.pageSize || 0));
      dispatch(setPage(data?.currentPage || 0));
    } else {
      dispatch(setIsTableLoading(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  return (
    <div>
      <UserListing
        columns={isCheckColumnPermission({
          moduleName: UserModuleNameTypes.USER,
          actionName: UserModuleActionTypes.LIST,
          columns: columns,
        })}
        rows={items}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
        onAddNew={() => setIsOpenAddForm(true)}
      />
      {isOpenAddForm ? (
        <AddUserFormWrapper onClose={() => setIsOpenAddForm(false)} />
      ) : null}
      {isOpenEditForm ? (
        <EditUserFormWrapper
          onClose={() => setIsOpenEditForm(false)}
          userId={selectedUserId}
        />
      ) : null}

      {isOpenChangePasswordDialog ? (
        <ChangePasswordDialogFormWrapper
        selectedUserId={selectedUserId}
          onClose={() => setIsOpenChangePasswordDialog(false)}
        />
      ) : null}
    </div>
  );
};

export default UserListingWrapper;
